'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import FadeInSection from './FadeInSection';

type Breakdown = { soap: number; appliance: number; total: number };
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/* ---- GA (local only) ---- */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return; // parent-only iframes: keep this
    const w: any = window;
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...(params || {}) });
      return;
    }
    // Fallback if gtag is still present in older deploys
    if (typeof w.gtag === 'function') {
      w.gtag('event', event, params || {});
    }
  } catch {}
}


/* ---- WP copy (optional overrides) ---- */
const WP_MARKETING_ENDPOINT = 'https://wateredgetech.com/wp-json/wet/v1/marketing';

export default function SavingsCalculator() {
  // Headline copy (can include HTML in sub)
  const [heading, setHeading] = useState('Calculate Your Savings');
  const [subHtml, setSubHtml] = useState('Simple estimate based on household size and water hardness.');

  // Field labels/helpers WP can override (keep sensible defaults)
  const [familyLabel, setFamilyLabel] = useState('Family Size');
  const [familyHelper, setFamilyHelper] = useState('Typical range 1–8. Uses $5/person/month for soaps and detergents.');
  const [hardnessLabel, setHardnessLabel] = useState('Water Hardness (gpg)');
  const [hardnessHelper, setHardnessHelper] = useState('0–3 soft, 4–7 moderately hard, 8–14 hard, 15+ very hard.');
  const [soapLabel, setSoapLabel] = useState('Soap & Detergent');
  const [applianceLabel, setApplianceLabel] = useState('Appliance Efficiency');
  const [estLabel, setEstLabel] = useState('Estimated Total');

  // Additional text content that is now overrideable
  const [modelNote, setModelNote] = useState('Model = $5×family×12 + $5×gpg. Round numbers for demo. Does not include energy savings or fewer service calls.');
  const [savingsTitle, setSavingsTitle] = useState('Estimated Annual Savings');
  const [whySavesTitleHtml, setWhySavesTitleHtml] = useState('<span class="text-teal-300 font-medium">Why it saves:</span>');
  const [whySavesList, setWhySavesList] = useState([
    'Softer water needs less soap to lather. You buy fewer soaps and detergents.',
    'Less scale on heaters, valves, and fixtures. Appliances run closer to spec for longer.',
    'Smoother rinse means less residue. Fewer re-washes and less product waste.',
  ]);
  const [finalDisclaimer, setFinalDisclaimer] = useState('This is a simple demo model, not a guarantee. Real savings vary by usage, product choices, and energy prices.');


  // Inputs
  const [familySize, setFamilySize] = useState(4);
  const [hardness, setHardness] = useState(10); // gpg

  // Gauge animation state
  const [targetPct, setTargetPct] = useState(0);
  const [displayPct, setDisplayPct] = useState(0);
  const rafRef = useRef<number | null>(null);

  // Fetch WP overrides once
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        const sec = data?.sections || {};

        const v = (s?: string) => (typeof s === 'string' && s.trim() ? s.trim() : undefined);
        if (!cancelled) {
          // Headline/sub
          const calcHeading = (sec.calc_heading ?? sec.why_heading) as string | undefined;
          const calcSub = (sec.calc_sub ?? sec.why_sub) as string | undefined;
          if (v(calcHeading)) setHeading(calcHeading!.trim());
          if (v(calcSub)) setSubHtml(calcSub!.trim());

          // Labels/helpers
          if (v(sec.calc_family_label)) setFamilyLabel(sec.calc_family_label.trim());
          if (v(sec.calc_family_helper)) setFamilyHelper(sec.calc_family_helper.trim());
          if (v(sec.calc_hardness_label)) setHardnessLabel(sec.calc_hardness_label.trim());
          if (v(sec.calc_hardness_helper)) setHardnessHelper(sec.calc_hardness_helper.trim());
          if (v(sec.calc_bucket_soap)) setSoapLabel(sec.calc_bucket_soap.trim());
          if (v(sec.calc_bucket_appliance)) setApplianceLabel(sec.calc_bucket_appliance.trim());
          if (v(sec.calc_bucket_total)) setEstLabel(sec.calc_bucket_total.trim());

          // Additional text content
          if (v(sec.calc_model_note)) setModelNote(sec.calc_model_note.trim());
          if (v(sec.calc_savings_title)) setSavingsTitle(sec.calc_savings_title.trim());
          if (v(sec.calc_why_saves_title)) setWhySavesTitleHtml(sec.calc_why_saves_title.trim());
          if (Array.isArray(sec.calc_why_saves_list) && sec.calc_why_saves_list.every((item: any) => typeof item === 'string')) {
            setWhySavesList(sec.calc_why_saves_list.filter((item: string) => item.trim()).map((item: string) => item.trim()));
          }
          if (v(sec.calc_final_disclaimer)) setFinalDisclaimer(sec.calc_final_disclaimer.trim());
        }

        ga('copy_override_loaded', { section: 'savings_calculator', ok: true });
      } catch {
        ga('copy_override_loaded', { section: 'savings_calculator', ok: false });
      }
    })();
    return () => { cancelled = true; ctrl.abort(); };
  }, []);

  // Simple model
  const breakdown: Breakdown = useMemo(() => {
    const soap = familySize * 5 * 12; // $5/person/month
    const appliance = hardness * 5;    // $5 per gpg/year
    const total = Math.round(soap + appliance);
    return { soap, appliance, total };
  }, [familySize, hardness]);

  // Update target gauge fill
  useEffect(() => {
    const MAX = 1000;
    setTargetPct(clamp(breakdown.total / MAX, 0, 1));
  }, [breakdown.total]);

  // Animate gauge fill
  useEffect(() => {
    const animate = () => {
      const diff = targetPct - displayPct;
      if (Math.abs(diff) < 0.001) {
        setDisplayPct(targetPct);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        return;
      }
      setDisplayPct((p) => p + diff * 0.12);
      rafRef.current = requestAnimationFrame(animate);
    };
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [targetPct, displayPct]);

  // Fire section view once (when visible)
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (fired) return;
        if (entries.some((e) => e.isIntersecting)) {
          fired = true;
          ga('section_view', { section: 'savings_calculator' });
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // GA: throttle input changes and result updates
  const lastSentRef = useRef<{ t: number; total: number }>({ t: 0, total: breakdown.total });
  useEffect(() => {
    const now = Date.now();
    const last = lastSentRef.current;
    const changedTotal = last.total !== breakdown.total;
    const tooSoon = now - last.t < 600;
    if (!tooSoon || changedTotal) {
      ga('calculator_change', {
        section: 'savings_calculator',
        family_size: familySize,
        hardness_gpg: hardness,
        est_total_usd: breakdown.total,
      });
      lastSentRef.current = { t: now, total: breakdown.total };
    }
  }, [familySize, hardness, breakdown.total]);

  // Emit result event when animation reaches target
  const sentResultRef = useRef<number | null>(null);
  useEffect(() => {
    if (Math.abs(displayPct - targetPct) < 0.002) {
      if (sentResultRef.current !== breakdown.total) {
        sentResultRef.current = breakdown.total;
        ga('calculator_result', {
          section: 'savings_calculator',
          est_total_usd: breakdown.total,
          soap_usd: breakdown.soap,
          appliance_usd: breakdown.appliance,
        });
      }
    }
  }, [displayPct, targetPct, breakdown.total, breakdown.soap, breakdown.appliance]);

  return (
    <section
      id="calculator"
      ref={sectionRef}
      // Added 'anchor-target' class here
      className="py-24 bg-[#0d0f22] anchor-target"
    >
      <FadeInSection>
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-2">{heading}</h2>
          <p
            className="text-center text-slate-400 mb-12"
            dangerouslySetInnerHTML={{ __html: subHtml }}
          />

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center bg-slate-800/30 p-6 md:p-8 rounded-xl">
            {/* Controls */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">{familyLabel}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min={1} max={8} step={1}
                    value={familySize}
                    onChange={(e) => setFamilySize(parseInt(e.target.value || '0'))}
                    className="w-full accent-teal-400"
                    onPointerUp={() =>
                      ga('slider_release', { section: 'savings_calculator', control: 'family_size', value: familySize })
                    }
                  />
                  <input
                    type="number" min={1} max={12}
                    value={familySize}
                    onChange={(e) => setFamilySize(clamp(parseInt(e.target.value || '0'), 1, 12))}
                    className="w-20 p-2 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500 text-right"
                    onBlur={() =>
                      ga('input_commit', { section: 'savings_calculator', control: 'family_size', value: familySize })
                    }
                    aria-label={familyLabel}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1" dangerouslySetInnerHTML={{ __html: familyHelper }} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">{hardnessLabel}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min={0} max={30} step={1}
                    value={hardness}
                    onChange={(e) => setHardness(parseInt(e.target.value || '0'))}
                    className="w-full accent-teal-400"
                    onPointerUp={() =>
                      ga('slider_release', { section: 'savings_calculator', control: 'hardness_gpg', value: hardness })
                    }
                  />
                  <input
                    type="number" min={0} max={60}
                    value={hardness}
                    onChange={(e) => setHardness(clamp(parseInt(e.target.value || '0'), 0, 60))}
                    className="w-20 p-2 rounded bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500 text-right"
                    onBlur={() =>
                      ga('input_commit', { section: 'savings_calculator', control: 'hardness_gpg', value: hardness })
                    }
                    aria-label={hardnessLabel}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1" dangerouslySetInnerHTML={{ __html: hardnessHelper }} />
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-slate-800/60 p-4 rounded-lg">
                  <p className="text-xs text-slate-400">{soapLabel}</p>
                  <p className="text-lg font-semibold text-teal-300">${breakdown.soap}</p>
                </div>
                <div className="bg-slate-800/60 p-4 rounded-lg">
                  <p className="text-xs text-slate-400">{applianceLabel}</p>
                  <p className="text-lg font-semibold text-teal-300">${breakdown.appliance}</p>
                </div>
                <div className="bg-slate-800/60 p-4 rounded-lg">
                  <p className="text-xs text-slate-400">{estLabel}</p>
                  <p className="text-lg font-semibold text-teal-300">${breakdown.total}</p>
                </div>
              </div>

              <div className="text-xs text-slate-500">
                {modelNote}
              </div>
            </div>

            {/* Gauge */}
            <div className="text-center">
              <style>{`
                @keyframes waveMove1 { 0% { transform: translateX(0); } 100% { transform: translateX(60px); } }
                @keyframes waveMove2 { 0% { transform: translateX(0); } 100% { transform: translateX(-60px); } }
                @keyframes shimmer { 0%,100% { opacity:.35 } 50% { opacity:.6 } }
              `}</style>

              <div className="relative w-48 h-72 mx-auto">
                <svg viewBox={`0 0 120 150`} className="w-full h-full">
                  <defs>
                    <mask id="bottleMask" maskUnits="userSpaceOnUse" x="0" y="0" width="120" height="150">
                      <rect x="0" y="0" width="120" height="150" fill="black" />
                      <g transform="translate(10,0) scale(0.9,1)">
                        <path d="M50 150 C 90 120, 100 80, 50 0 C 0 80, 10 120, 50 150 Z" fill="white" />
                      </g>
                    </mask>
                    <linearGradient id="waterGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#7dd3fc"/>
                      <stop offset="100%" stopColor="#22d3ee"/>
                    </linearGradient>
                    <linearGradient id="shineGrad" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity="0"/>
                      <stop offset="40%" stopColor="#ffffff" stopOpacity=".08"/>
                      <stop offset="100%" stopColor="#ffffff" stopOpacity="0"/>
                    </linearGradient>
                  </defs>

                  {/* bottle outline */}
                  <g transform="translate(10,0) scale(0.9,1)">
                    <path d="M50 150 C 90 120, 100 80, 50 0 C 0 80, 10 120, 50 150 Z" fill="#0f172a" />
                  </g>

                  {/* masked content */}
                  <g mask="url(#bottleMask)">
                    <rect x="0" y="0" width="120" height="150" fill="#ffffff" />
                    <g transform={`translate(0, ${clamp((1 - displayPct) * 100, 0, 100)})`}>
                      <rect x="0" y="-100" width="120" height={150 + 100} fill="url(#waterGrad)" />
                      <g style={{ animation: 'waveMove1 4.5s linear infinite' }}>
                        <path d="M -120 30 Q -105 20 -90 30 T -60 30 T -30 30 T 0 30 T 30 30 T 60 30 T 90 30 T 120 30"
                              fill="none" stroke="#67e8f9" strokeWidth="8" strokeLinecap="round" opacity=".45"/>
                      </g>
                      <g style={{ animation: 'waveMove2 6s linear infinite' }}>
                        <path d="M -120 40 Q -105 30 -90 40 T -60 40 T -30 40 T 0 40 T 30 40 T 60 40 T 90 40 T 120 40"
                              fill="none" stroke="#38bdf8" strokeWidth="8" strokeLinecap="round" opacity=".35"/>
                      </g>
                    </g>
                    <g transform="translate(10,0) scale(0.9,1)">
                      <rect x="22" y="8" width="10" height="134" fill="url(#shineGrad)" style={{ animation: 'shimmer 3s ease-in-out infinite' }}/>
                    </g>
                  </g>

                  {/* meniscus highlight */}
                  <g transform="translate(10,0) scale(0.9,1)">
                    <path
                      d={`M 8 ${clamp((1 - displayPct) * 100 + 30, 0, 140)} Q 28 ${clamp((1 - displayPct) * 100 + 28, 0, 140)} 48 ${clamp((1 - displayPct) * 100 + 30, 0, 140)} T 88 ${clamp((1 - displayPct) * 100 + 30, 0, 140)}`}
                      fill="none" stroke="#ffffff" strokeOpacity=".35" strokeWidth="1.5"
                    />
                  </g>

                  {/* ticks */}
                  {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                    <g key={t}>
                      <line x1="2" x2="12" y1={150 - t * 120 - 15} y2={150 - t * 120 - 15} stroke="#1f2937" strokeWidth="2" />
                      <text x="0" y={150 - t * 120 - 18} fontSize="8" fill="#94a3b8" textAnchor="start">
                        {Math.round(t * 100)}%
                      </text>
                    </g>
                  ))}
                </svg>
              </div>

              <p className="text-lg mt-4">{savingsTitle}</p>
              <p className="text-4xl font-bold text-teal-400">${breakdown.total}</p>

              <div className="mt-4 text-left mx-auto max-w-sm text-slate-300 text-sm space-y-2">
                <p dangerouslySetInnerHTML={{ __html: whySavesTitleHtml }} />
                <ul className="list-disc pl-5 space-y-1">
                  {whySavesList.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              </div>

              <p className="mt-3 text-xs text-slate-500">
                {finalDisclaimer}
              </p>
            </div>
          </div>
        </div>
      </FadeInSection>
    </section>
  );
}